import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Search, X, User, Building2, Clock, ArrowRight, Mail, UserPlus, UserCheck, Hash, MessageSquare, Users, MapPin, Globe, Star, FileText } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from './AuthContext';
import { BACKEND_URL as GREENWICH_BACKEND_URL } from '../utils/network.js';
import { localizeMessage } from '../utils/toast.js';
import './SearchModal.css';

function SearchModal({ isOpen, onClose }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState({
    users: [],
    posts: [],
    organizations: [],
    players: [],
    communities: []
  });
  const [activeTab, setActiveTab] = useState('all');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [recentSearches, setRecentSearches] = useState([]);
  const [following, setFollowing] = useState([]);
  const [isLoadingFollow, setIsLoadingFollow] = useState({});
  const inputRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (isOpen) {
      const saved = localStorage.getItem('recentSearches');
      if (saved) {
        setRecentSearches(JSON.parse(saved));
      }
      if (user) {
        loadFollowing();
      }
      
      // Focus input
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen, user]);

  const loadFollowing = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await fetch(`${GREENWICH_BACKEND_URL}/api/users/following`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setFollowing(data.following || data || []);
      }
    } catch (error) {
      console.error('Error loading following:', error);
    }
  };

  const handleFollow = async (userId, e) => {
    e.stopPropagation();
    
    if (!userId || userId === 'undefined' || userId === 'null') {
      console.error('Invalid userId:', userId);
      return;
    }
    
    try {
      setIsLoadingFollow(prev => ({ ...prev, [userId]: true }));
      const token = localStorage.getItem('token');
      if (!token) return;
      
      const isFollowingUser = following.some(u => 
        u._id === userId || (typeof u === 'object' && u._id === userId)
      );
      
      const endpoint = isFollowingUser ? 'unfollow' : 'follow';
      const method = isFollowingUser ? 'DELETE' : 'POST';
      
      const response = await fetch(`${GREENWICH_BACKEND_URL}/api/users/${userId}/${endpoint}`, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        if (isFollowingUser) {
          setFollowing(following.filter(u => 
            u._id !== userId && (typeof u !== 'object' || u._id !== userId)
          ));
        } else {
          const newUser = searchResults.users.find(u => u._id === userId) ||
                         searchResults.organizations.find(u => u._id === userId) ||
                         searchResults.players.find(u => u._id === userId);
          if (newUser) {
            setFollowing([...following, newUser]);
          }
        }
        loadFollowing();
      }
    } catch (error) {
      console.error('Error following user:', error);
    } finally {
      setIsLoadingFollow(prev => ({ ...prev, [userId]: false }));
    }
  };

  const isFollowingUser = (userId) => {
    return following.some(u => 
      u._id === userId || (typeof u === 'object' && u._id === userId)
    );
  };

  const performSearch = async (query) => {
    if (!query.trim()) {
      setSearchResults({ users: [], posts: [], organizations: [], players: [], communities: [] });
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${GREENWICH_BACKEND_URL}/api/search?q=${encodeURIComponent(query)}&limit=20&type=all`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        
        if (data.success === false) {
          setError(data.error || t('searchModal.searchFailed'));
          setSearchResults({
            users: [],
            posts: [],
            organizations: [],
            players: [],
            communities: []
          });
        } else {
          // Process users to include counts
          const processedUsers = (data.organizers || []).map(user => ({
            ...user,
            followersCount: user.followersCount || user.followers?.length || 0,
            postsCount: user.postsCount || user.posts?.length || 0
          }));

          const processedPlayers = (data.players || []).map(player => ({
            ...player,
            followersCount: player.followersCount || player.followers?.length || 0,
            postsCount: player.postsCount || player.posts?.length || 0
          }));

          const processedRegularUsers = (data.users || []).map(user => ({
            ...user,
            followersCount: user.followersCount || user.followers?.length || 0,
            postsCount: user.postsCount || user.posts?.length || 0
          }));
          
          // data.users already contains every matched user (organizers and players
          // included) — data.organizers/data.players are just filtered subsets of it for
          // the tab views below, so concatenating all three used to list the same person
          // multiple times. Dedupe by _id to keep one entry per user.
          const seenUserIds = new Set();
          const allUsers = [
            ...processedUsers,
            ...processedPlayers,
            ...processedRegularUsers
          ].filter(user => {
            if (!user._id || seenUserIds.has(user._id)) return false;
            seenUserIds.add(user._id);
            return true;
          });
          
          setSearchResults({
            users: allUsers,
            posts: data.posts || [],
            organizations: processedUsers,
            players: processedPlayers,
            communities: data.communities || data.groups || []
          });

          if (query.trim()) {
            const newRecent = [query, ...recentSearches.filter(s => s !== query)].slice(0, 5);
            setRecentSearches(newRecent);
            localStorage.setItem('recentSearches', JSON.stringify(newRecent));
          }

          // Best-effort search logging — never blocks or surfaces errors to the user
          const loggedResultsCount = allUsers.length + (data.posts || []).length + (data.communities || data.groups || []).length;
          fetch(`${GREENWICH_BACKEND_URL}/api/search/log`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...(token ? { Authorization: `Bearer ${token}` } : {})
            },
            body: JSON.stringify({ query, resultsCount: loggedResultsCount })
          }).catch(() => {});
        }
      } else {
        const errorData = await response.json().catch(() => ({
          error: response.status === 500 ? t('searchModal.serverError') : t('searchModal.searchFailed')
        }));
        console.error('Search API error:', errorData);
        setError(errorData.error || t('searchModal.searchFailed'));
        setSearchResults({
          users: [],
          posts: [],
          organizations: [],
          players: [],
          communities: []
        });
      }
    } catch (error) {
      console.error('Search error:', error);
      const errorMessage = error.message.includes('Failed to fetch')
        ? t('searchModal.cannotConnect')
        : t('searchModal.networkError');
      setError(errorMessage);
      setSearchResults({
        users: [],
        posts: [],
        organizations: [],
        players: [],
        communities: []
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    setError(null);
    
    if (value.trim()) {
      clearTimeout(window.searchTimeout);
      window.searchTimeout = setTimeout(() => {
        performSearch(value);
      }, 300);
    } else {
      setSearchResults({ users: [], posts: [], organizations: [], players: [], communities: [] });
    }
  };

  const handleClearRecent = () => {
    setRecentSearches([]);
    localStorage.removeItem('recentSearches');
  };

  const handleResultClick = (item, type) => {
    onClose();
    
    const itemId = item._id || item.id;
    
    switch(type) {
      case 'user':
        navigate(`/profile/${itemId}`);
        break;
      case 'organization':
        navigate(`/organization/${itemId}`);
        break;
      case 'post':
        navigate(`/post/${itemId}`);
        break;
      case 'community':
        navigate(`/community/group/${itemId}`);
        break;
      default:
        navigate(`/profile/${itemId}`);
    }
  };

  const handleSeeAllResults = () => {
    onClose();
    navigate(`/search?q=${encodeURIComponent(searchQuery)}&type=${activeTab}`);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      onClose();
    } else if (e.key === 'Enter' && searchQuery.trim()) {
      performSearch(searchQuery);
    }
  };

  const getImageUrl = (imagePath) => {
    if (!imagePath || imagePath === 'null' || imagePath === 'undefined' || imagePath === '' || imagePath === null) {
      return '/nkarner/20260110_1932_Image Generation_remix_01kem8h20rewa81f8h55ncqmm9 (2).png';
    }

    if (imagePath.startsWith('blob:') || imagePath.startsWith('data:')) {
      return imagePath;
    }
    
    if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
      return imagePath;
    }
    
    if (imagePath.startsWith('/uploads/')) {
      return `${GREENWICH_BACKEND_URL}${imagePath}`;
    }
    
    if (imagePath.startsWith('/')) {
      return imagePath;
    }
    
    if (imagePath.includes('uploads/')) {
      const idx = imagePath.indexOf('uploads/');
      const relativePath = imagePath.substring(idx);
      return `${GREENWICH_BACKEND_URL}/${relativePath}`;
    }
    
    return `${GREENWICH_BACKEND_URL}/uploads/${imagePath}`;
  };

  const formatNumber = (num) => {
    if (!num) return '0';
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num.toString();
  };

  if (!isOpen) return null;

  return (
    <div className="search-modal-overlay clay-modal-backdrop" onClick={onClose}>
      <div className="search-modal" onClick={(e) => e.stopPropagation()} onKeyDown={handleKeyDown}>
        {/* Search Header */}
        <div className="search-modal-header">
          <div className="search-input-wrapper">
            <Search size={20} className="search-icon" />
            <input
              ref={inputRef}
              type="text"
              placeholder={t('searchModal.placeholder')}
              value={searchQuery}
              onChange={handleSearchChange}
              className="search-modal-input"
              autoFocus
            />
            {searchQuery && (
              <button 
                className="clear-search-btn"
                onClick={() => {
                  setSearchQuery('');
                  setSearchResults({ users: [], posts: [], organizations: [], players: [], communities: [] });
                }}
              >
                <X size={16} />
              </button>
            )}
          </div>
          <button className="close-modal-btn" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        {/* Search Tabs */}
        {searchQuery && (
          <div className="search-tabs">
            <button 
              className={`search-tab ${activeTab === 'all' ? 'active' : ''}`}
              onClick={() => setActiveTab('all')}
            >
              <Search size={14} />
              {t('searchModal.all')}
            </button>
            <button
              className={`search-tab ${activeTab === 'organizations' ? 'active' : ''}`}
              onClick={() => setActiveTab('organizations')}
            >
              <Building2 size={14} />
              {t('searchModal.organizations')}
            </button>
            <button
              className={`search-tab ${activeTab === 'players' ? 'active' : ''}`}
              onClick={() => setActiveTab('players')}
            >
              <User size={14} />
              {t('searchModal.players')}
            </button>
            <button
              className={`search-tab ${activeTab === 'posts' ? 'active' : ''}`}
              onClick={() => setActiveTab('posts')}
            >
              <MessageSquare size={14} />
              {t('searchModal.posts')}
            </button>
            <button
              className={`search-tab ${activeTab === 'communities' ? 'active' : ''}`}
              onClick={() => setActiveTab('communities')}
            >
              <Users size={14} />
              {t('searchModal.communities')}
            </button>
          </div>
        )}

        <div className="search-modal-content clay-modal-enter">
          {/* Loading State */}
          {loading && (
            <div className="search-loading">
              <div className="loading-spinner"></div>
              <span>{t('searchModal.searching')}</span>
            </div>
          )}

          {/* Error Message */}
          {error && !loading && (
            <div className="search-error">
              <span>⚠️</span>
              <span>{localizeMessage(error)}</span>
              <button onClick={() => setError(null)}>
                <X size={16} />
              </button>
            </div>
          )}

          {/* Search Results */}
          {!loading && searchQuery && !error && (
            <div className="search-results">
              {/* Users Results */}
              {searchResults.users.length > 0 && 
               (activeTab === 'all' || activeTab === 'players') && (
                <div className="results-section">
                  <div className="section-header">
                    <h4><User size={16} /> {t('searchModal.users')}</h4>
                    <span className="result-count">{searchResults.users.length}</span>
                  </div>
                  <div className="results-list">
                    {searchResults.users.slice(0, 3).map(userItem => (
                      <UserResultItem 
                        key={userItem._id || userItem.id}
                        user={userItem}
                        onClick={() => handleResultClick(userItem, 'user')}
                        onFollow={(e) => {
                          const userId = userItem._id || userItem.id;
                          if (userId) {
                            handleFollow(userId, e);
                          }
                        }}
                        isFollowing={isFollowingUser(userItem._id || userItem.id)}
                        isLoading={isLoadingFollow[userItem._id || userItem.id]}
                        currentUserId={user?._id}
                        showFollowButton={true}
                        formatNumber={formatNumber}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Organizations Results */}
              {searchResults.organizations.length > 0 && 
               (activeTab === 'all' || activeTab === 'organizations') && (
                <div className="results-section">
                  <div className="section-header">
                    <h4><Building2 size={16} /> {t('searchModal.organizations')}</h4>
                    <span className="result-count">{searchResults.organizations.length}</span>
                  </div>
                  <div className="results-list">
                    {searchResults.organizations.slice(0, 3).map(org => (
                      <UserResultItem 
                        key={org._id || org.id}
                        user={org}
                        onClick={() => handleResultClick(org, 'organization')}
                        onFollow={(e) => {
                          const userId = org._id || org.id;
                          if (userId) {
                            handleFollow(userId, e);
                          }
                        }}
                        isFollowing={isFollowingUser(org._id || org.id)}
                        isLoading={isLoadingFollow[org._id || org.id]}
                        currentUserId={user?._id}
                        showFollowButton={true}
                        formatNumber={formatNumber}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Posts Results */}
              {searchResults.posts.length > 0 && 
               (activeTab === 'all' || activeTab === 'posts') && (
                <div className="results-section">
                  <div className="section-header">
                    <h4><MessageSquare size={16} /> {t('searchModal.posts')}</h4>
                    <span className="result-count">{searchResults.posts.length}</span>
                  </div>
                  <div className="results-list">
                    {searchResults.posts.slice(0, 2).map(post => (
                      <PostResultItem 
                        key={post._id || post.id}
                        post={post}
                        onClick={() => handleResultClick(post, 'post')}
                        getImageUrl={getImageUrl}
                        formatNumber={formatNumber}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Communities Results */}
              {searchResults.communities.length > 0 && 
               (activeTab === 'all' || activeTab === 'communities') && (
                <div className="results-section">
                  <div className="section-header">
                    <h4><Users size={16} /> {t('searchModal.communities')}</h4>
                    <span className="result-count">{searchResults.communities.length}</span>
                  </div>
                  <div className="results-list">
                    {searchResults.communities.slice(0, 3).map(community => (
                      <CommunityResultItem 
                        key={community._id || community.id}
                        community={community}
                        onClick={() => handleResultClick(community, 'community')}
                        getImageUrl={getImageUrl}
                        formatNumber={formatNumber}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* No Results */}
              {!loading && searchQuery && !error &&
               searchResults.users.length === 0 && 
               searchResults.posts.length === 0 && 
               searchResults.organizations.length === 0 && 
               searchResults.players.length === 0 &&
               searchResults.communities.length === 0 && (
                <div className="no-results">
                  <div className="no-results-icon">🔍</div>
                  <p>{t('searchModal.noResultsFor', { query: searchQuery })}</p>
                  <span>{t('searchModal.tryDifferentKeywords')}</span>
                </div>
              )}

              {/* See All Results */}
              {searchQuery && !error && (
                <div className="see-all-section">
                  <button className="see-all-results" onClick={handleSeeAllResults}>
                    {t('searchModal.seeAllResultsFor', { query: searchQuery })}
                    <ArrowRight size={16} />
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Recent Searches */}
          {!loading && !searchQuery && !error && recentSearches.length > 0 && (
            <div className="recent-searches-section">
              <div className="section-header">
                <h4>{t('searchModal.recentSearches')}</h4>
                <button onClick={handleClearRecent} className="clear-recent-btn">
                  {t('searchModal.clearAll')}
                </button>
              </div>
              <div className="recent-searches-list">
                {recentSearches.map((search, index) => (
                  <button
                    key={index}
                    className="recent-search-item"
                    onClick={() => {
                      setSearchQuery(search);
                      performSearch(search);
                    }}
                  >
                    <Clock size={16} />
                    <span>{search}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Empty State */}
          {!loading && !searchQuery && !error && recentSearches.length === 0 && (
            <div className="empty-state">
              <div className="empty-icon">🔍</div>
              <h4>{t('searchModal.searchPlatform')}</h4>
              <p>{t('searchModal.findPeopleEtc')}</p>
              <div className="search-suggestions">
                <span className="suggestion-tag">
                  <Building2 size={12} /> {t('searchModal.organizations')}
                </span>
                <span className="suggestion-tag">
                  <User size={12} /> {t('searchModal.players')}
                </span>
                <span className="suggestion-tag">
                  <MessageSquare size={12} /> {t('searchModal.posts')}
                </span>
                <span className="suggestion-tag">
                  <Users size={12} /> {t('searchModal.communities')}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// User Result Item Component - Updated with followers and posts count
function UserResultItem({ user, onClick, onFollow, isFollowing, isLoading, currentUserId, showFollowButton = true, formatNumber }) {
  const { t } = useTranslation();
  const getUserTypeIcon = () => {
    switch (user.userType || user.accountType) {
      case 'organizer':
      case 'organization':
        return <Building2 size={14} />;
      case 'admin':
        return <Hash size={14} />;
      default:
        return <User size={14} />;
    }
  };

  const getUserTypeLabel = () => {
    switch (user.userType || user.accountType) {
      case 'organizer':
      case 'organization':
        return t('searchModal.organization');
      case 'admin':
        return t('searchModal.administrator');
      default:
        return t('searchModal.communityMember');
    }
  };

  const getImageUrl = (imagePath) => {
    if (!imagePath || imagePath === 'null' || imagePath === 'undefined' || imagePath === '' || imagePath === null) {
      return '/nkarner/20260110_1932_Image Generation_remix_01kem8h20rewa81f8h55ncqmm9 (2).png';
    }

    if (imagePath.startsWith('blob:') || imagePath.startsWith('data:')) {
      return imagePath;
    }
    
    if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
      return imagePath;
    }
    
    if (imagePath.startsWith('/uploads/')) {
      return `${GREENWICH_BACKEND_URL}${imagePath}`;
    }
    
    if (imagePath.startsWith('/')) {
      return imagePath;
    }
    
    if (imagePath.includes('uploads/')) {
      const idx = imagePath.indexOf('uploads/');
      const relativePath = imagePath.substring(idx);
      return `${GREENWICH_BACKEND_URL}/${relativePath}`;
    }
    
    return `${GREENWICH_BACKEND_URL}/uploads/${imagePath}`;
  };

  return (
    <div className="result-item user-result">
      <button className="result-item-main clay-press" onClick={onClick}>
        <div style={{ position: 'relative', display: 'inline-block' }}>
          <img 
            src={getImageUrl(user.profileImage || user.avatar)} 
            alt={user.organizationName || user.name}
            className="result-avatar"
            onError={(e) => {
              const defaultAvatar = '/nkarner/20260110_1932_Image Generation_remix_01kem8h20rewa81f8h55ncqmm9 (2).png';
              if (e.target.src !== defaultAvatar) {
                e.target.src = defaultAvatar;
              }
            }}
            loading="lazy"
          />
          {user.isVerified && (
            <Star 
              style={{
                position: 'absolute',
                bottom: '-2px',
                right: '-2px',
                width: '18px',
                height: '18px',
                color: '#FFD700',
                fill: '#FFD700',
                backgroundColor: 'white',
                borderRadius: '50%',
                padding: '2px',
                zIndex: 10,
                boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                pointerEvents: 'none'
              }}
              title={t('searchModal.verifiedOrganization')}
            />
          )}
        </div>
        <div className="result-content">
          <div className="result-title">
            {user.organizationName || `${user.name || ''} ${user.surname || ''}`.trim() || user.username || user.email}
          </div>
          <div className="result-subtitle">
            {getUserTypeIcon()}
            <span>{getUserTypeLabel()}</span>
            {user.location && (
              <>
                <span>•</span>
                <span><MapPin size={12} /> {user.location}</span>
              </>
            )}
          </div>
          
          {/* Followers and Posts Count */}
          <div className="result-stats">
            <span>
              <Users size={12} />
              {formatNumber(user.followersCount || user.followers?.length || 0)} {t('searchModal.followers')}
            </span>
            <span>
              <FileText size={12} />
              {formatNumber(user.postsCount || user.posts?.length || 0)} {t('searchModal.postsCount')}
            </span>
          </div>

          {user.bio && (
            <div className="result-bio">
              {user.bio.length > 60 ? `${user.bio.substring(0, 60)}...` : user.bio}
            </div>
          )}
        </div>
      </button>
      {showFollowButton && onFollow && currentUserId !== user._id && (
        <button
          className={`follow-btn-search ${isFollowing ? 'following' : ''}`}
          onClick={onFollow}
          disabled={isLoading}
        >
          {isLoading ? (
            <span className="loading-dots">...</span>
          ) : isFollowing ? (
            <>
              <UserCheck size={14} />
              <span>{t('searchModal.following')}</span>
            </>
          ) : (
            <>
              <UserPlus size={14} />
              <span>{t('searchModal.follow')}</span>
            </>
          )}
        </button>
      )}
    </div>
  );
}

// Post Result Item Component - Updated with post image
function PostResultItem({ post, onClick, getImageUrl, formatNumber }) {
  const { t } = useTranslation();
  const formatDate = (date) => {
    if (!date) return '';
    const d = new Date(date);
    const now = new Date();
    const diff = now - d;
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (hours < 24) {
      return t('searchModal.hAgo', { n: hours });
    } else if (days < 7) {
      return t('searchModal.dAgo', { n: days });
    } else {
      return d.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric' 
      });
    }
  };

  // Get post image URL
  const getPostImageUrl = (imagePath) => {
    if (!imagePath || imagePath === 'null' || imagePath === 'undefined' || imagePath === '') {
      return null;
    }

    if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
      return imagePath;
    }
    
    if (imagePath.startsWith('/uploads/')) {
      return `${GREENWICH_BACKEND_URL}${imagePath}`;
    }
    
    if (imagePath.includes('uploads/')) {
      const idx = imagePath.indexOf('uploads/');
      const relativePath = imagePath.substring(idx);
      return `${GREENWICH_BACKEND_URL}/${relativePath}`;
    }
    
    return null;
  };

  const postImage = post.image || post.images?.[0] || post.media?.[0];

  return (
    <button className="result-item post-result clay-press" onClick={onClick}>
      <div className="post-result-avatar-container">
        <div style={{ position: 'relative', display: 'inline-block' }}>
          <img 
            src={getImageUrl(post.author?.profileImage || post.author?.avatar)} 
            alt={post.author?.organizationName || post.author?.name}
            className="result-avatar"
            onError={(e) => {
              const defaultAvatar = '/nkarner/20260110_1932_Image Generation_remix_01kem8h20rewa81f8h55ncqmm9 (2).png';
              e.target.src = defaultAvatar;
            }}
            loading="lazy"
          />
          {post.author?.isVerified && (
            <Star 
              style={{
                position: 'absolute',
                bottom: '-2px',
                right: '-2px',
                width: '18px',
                height: '18px',
                color: '#FFD700',
                fill: '#FFD700',
                backgroundColor: 'white',
                borderRadius: '50%',
                padding: '2px',
                zIndex: 10,
                boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
              }}
            />
          )}
        </div>
      </div>
      <div className="result-content">
        <div className="result-title">
          {post.author?.organizationName || post.author?.name || t('searchModal.user')}
        </div>
        <div className="result-subtitle">
          {post.content ?
            (post.content.length > 80 ? `${post.content.substring(0, 80)}...` : post.content) :
            (post.title || t('searchModal.noContent'))
          }
        </div>
        
        {/* Post Image Thumbnail */}
        {postImage && (
          <div className="post-thumbnail-container">
            <img 
              src={getPostImageUrl(postImage)}
              alt={t('searchModal.postThumbnail')}
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
          </div>
        )}

        <div className="result-meta">
          <span>{formatDate(post.createdAt || post.created_at)}</span>
          <span>•</span>
          <span>{formatNumber(post.likes?.length || post.likesCount || 0)} {t('searchModal.likes')}</span>
          <span>•</span>
          <span>{formatNumber(post.comments?.length || post.commentsCount || 0)} {t('searchModal.comments')}</span>
        </div>
      </div>
    </button>
  );
}

// Community Result Item Component
function CommunityResultItem({ community, onClick, getImageUrl, formatNumber }) {
  const { t } = useTranslation();
  return (
    <button className="result-item community-result clay-press" onClick={onClick}>
      <img 
        src={getImageUrl(community.image || community.profileImage || community.avatar)} 
        alt={community.name}
        className="result-avatar community-avatar"
        onError={(e) => {
          const defaultAvatar = '/nkarner/20260110_1932_Image Generation_remix_01kem8h20rewa81f8h55ncqmm9 (2).png';
          e.target.src = defaultAvatar;
        }}
        loading="lazy"
      />
      <div className="result-content">
        <div className="result-title">
          {community.name}
          {community.isPrivate && (
            <span className="private-badge" title={t('searchModal.privateCommunity')}>🔒</span>
          )}
        </div>
        <div className="result-subtitle">
          <Users size={14} />
          <span>{t('searchModal.community')}</span>
          <span>•</span>
          <span>{formatNumber(community.members?.length || community.memberCount || 0)} {t('searchModal.members')}</span>
        </div>
        {community.description && (
          <div className="result-bio">
            {community.description.length > 60 ? 
              `${community.description.substring(0, 60)}...` : 
              community.description}
          </div>
        )}
      </div>
    </button>
  );
}

export default SearchModal;
